import { useState } from 'react';
import { Surface, Badge, Button, Grid, PageHeader, Input, Select, Modal, Checkbox } from '../../ui/components';
import { Plus, Edit } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const mockConditions = [
  { id: '1', name: 'Pay Stubs (Last 2)', category: 'Income', description: 'Most recent 2 pay stubs', is_required: true, order: 1 },
  { id: '2', name: 'W2 Forms (2 Years)', category: 'Income', description: 'Last 2 years W2 forms', is_required: true, order: 2 },
  { id: '3', name: 'Bank Statements (2 Months)', category: 'Assets', description: 'Last 2 months bank statements', is_required: true, order: 3 },
  { id: '4', name: 'Tax Returns (2 Years)', category: 'Income', description: 'Last 2 years tax returns', is_required: true, order: 4 },
  { id: '5', name: 'Photo ID', category: 'Identity', description: 'Government-issued photo ID', is_required: true, order: 5 },
  { id: '6', name: 'Purchase Contract', category: 'Property', description: 'Signed purchase agreement', is_required: true, order: 6 },
];

const AdminConditions = () => {
  const [conditions] = useState(mockConditions);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (condition) => {
    setSelectedCondition(condition);
    setEditOpen(true);
  };

  const categories = [...new Set(conditions.map(c => c.category))];

  return (
    <div>
      <PageHeader
        title="Document Conditions"
        description="Manage required document conditions"
        actions={<Button variant="primary" icon={Plus}>Add Condition</Button>}
      />

      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Conditions</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{conditions.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Required</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-danger-600)' }}>{conditions.filter(c => c.is_required).length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Categories</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{categories.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Optional</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>{conditions.filter(c => !c.is_required).length}</div>
        </Surface>
      </Grid>

      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Condition</th>
                <th>Category</th>
                <th>Description</th>
                <th>Required</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conditions.sort((a, b) => a.order - b.order).map((condition) => (
                <tr key={condition.id}>
                  <td style={{ fontWeight: 600 }}>{condition.order}</td>
                  <td style={{ fontWeight: 500 }}>{condition.name}</td>
                  <td><Badge variant="neutral">{condition.category}</Badge></td>
                  <td style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>{condition.description}</td>
                  <td>
                    {condition.is_required ? (
                      <Badge variant="danger">Required</Badge>
                    ) : (
                      <Badge variant="primary">Optional</Badge>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Button size="sm" variant="ghost" icon={Edit} onClick={() => handleEdit(condition)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Condition"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary">Save Changes</Button>
          </>
        }
      >
        {selectedCondition && (
          <>
            <Input label="Condition Name" defaultValue={selectedCondition.name} />
            <Select label="Category" defaultValue={selectedCondition.category} style={{ marginTop: 'var(--lk-space-3)' }}>
              <option value="Income">Income</option>
              <option value="Assets">Assets</option>
              <option value="Identity">Identity</option>
              <option value="Property">Property</option>
              <option value="Credit">Credit</option>
            </Select>
            <Input label="Description" defaultValue={selectedCondition.description} style={{ marginTop: 'var(--lk-space-3)' }} />
            <Input label="Display Order" type="number" defaultValue={selectedCondition.order} style={{ marginTop: 'var(--lk-space-3)' }} />
            <Checkbox label="Required Document" checked={selectedCondition.is_required} onChange={() => {}} style={{ marginTop: 'var(--lk-space-3)' }} />
          </>
        )}
      </Modal>

      <FloatingOwenAssistant context="admin-conditions" />
      <FloatingKnowledge customContent={{
        label: 'Conditions',
        title: 'Condition templates',
        body: 'Manage standard underwriting condition templates.',
        bullets: ['Create condition templates', 'Standardize requirements', 'Track condition usage'],
        resources: [{ label: 'Condition library', href: '#' }],
      }} />
    </div>
  );
};

export default AdminConditions;
